import { arrayMove } from '@dnd-kit/sortable'
import { ItemClass, ItemClasses } from '../../../common/constants'
import {
  createCuratedTemplateDraft,
  createCuratedTemplateItem,
  CuratedTemplate,
  CuratedTemplateAvailability,
  CuratedTemplateItem,
  CuratedTemplateUpsertRequest
} from '../../../domain/curatedTemplates/curatedTemplate'
import { ProductionRate } from '../../../domain/productionRate'
import { hasValue } from '../../../helpers/nullable'

export type EditorState = {
  original: CuratedTemplate
  draft: CuratedTemplate
}

export type EditorAction =
  | { type: 'NAME_UPDATED', value: string }
  | { type: 'DESCRIPTION_UPDATED', value: string }
  | { type: 'AVAILABILITY_UPDATED', value: CuratedTemplateAvailability }
  | { type: 'AI_INSTRUCTIONS_UPDATED', value: string }
  | { type: 'ITEM_ADDED' }
  | { type: 'ITEM_UPDATED', curatedTemplateItemId: string, changes: Partial<CuratedTemplateItem> }
  | { type: 'NAME_ALIASES_UPDATED', curatedTemplateItemId: string, nameAliases: string[] }
  | { type: 'ITEM_REMOVED', curatedTemplateItemId: string }
  | { type: 'ITEMS_REORDERED', oldIndex: number, newIndex: number }
  | { type: 'PRODUCTION_RATE_UPDATED', curatedTemplateItemId: string, productionRate: ProductionRate }
  | { type: 'PRODUCTION_RATE_CLEARED', curatedTemplateItemId: string }

export const curatedItemClassOptions: { label: string, value: ItemClass }[] = [
  { label: 'Labor', value: ItemClasses.Labor },
  { label: 'Equipment / Vehicles', value: ItemClasses.Equipment },
  { label: 'Materials', value: ItemClasses.Materials },
  { label: 'Subcontracting', value: ItemClasses.Subcontracting }
]

export const sortedItems = (items: CuratedTemplateItem[]): CuratedTemplateItem[] => {
  return [...items].sort((a, b) => a.sortIndex - b.sortIndex)
}

const reindex = (items: CuratedTemplateItem[]): CuratedTemplateItem[] => {
  return items.map((item, sortIndex) => ({ ...item, sortIndex }))
}

export const reorderItems = (items: CuratedTemplateItem[], oldIndex: number, newIndex: number): CuratedTemplateItem[] => {
  return reindex(arrayMove(sortedItems(items), oldIndex, newIndex))
}

export const createEditorState = (template: CuratedTemplate): EditorState => {
  const ordered = { ...template, items: reindex(sortedItems(template.items)) }
  return { original: ordered, draft: ordered }
}

export const createEditorStateFrom = (template: CuratedTemplate | undefined): EditorState => {
  return createEditorState(template ?? createCuratedTemplateDraft())
}

const withItems = (state: EditorState, items: CuratedTemplateItem[]): EditorState => {
  return { ...state, draft: { ...state.draft, items } }
}

const mapItem = (
  items: CuratedTemplateItem[],
  curatedTemplateItemId: string,
  update: (item: CuratedTemplateItem) => CuratedTemplateItem
): CuratedTemplateItem[] => {
  return items.map(item => item.curatedTemplateItemId === curatedTemplateItemId ? update(item) : item)
}

export const curatedTemplateEditorReducer = (state: EditorState, action: EditorAction): EditorState => {
  switch (action.type) {
    case 'NAME_UPDATED':
      return { ...state, draft: { ...state.draft, name: action.value } }
    case 'DESCRIPTION_UPDATED':
      return { ...state, draft: { ...state.draft, description: action.value } }
    case 'AVAILABILITY_UPDATED':
      return { ...state, draft: { ...state.draft, availability: action.value } }
    case 'AI_INSTRUCTIONS_UPDATED':
      return { ...state, draft: { ...state.draft, aiInstructions: action.value } }
    case 'ITEM_ADDED':
      return withItems(state, [...sortedItems(state.draft.items), createCuratedTemplateItem(state.draft.items.length)])
    case 'ITEM_UPDATED':
      return withItems(state, mapItem(state.draft.items, action.curatedTemplateItemId, item => ({
        ...item,
        ...action.changes
      })))
    case 'NAME_ALIASES_UPDATED':
      return withItems(state, mapItem(state.draft.items, action.curatedTemplateItemId, item => ({
        ...item,
        nameAliases: normalizeNameAliases(action.nameAliases)
      })))
    case 'ITEM_REMOVED':
      return withItems(state, reindex(sortedItems(state.draft.items).filter(x => x.curatedTemplateItemId !== action.curatedTemplateItemId)))
    case 'ITEMS_REORDERED':
      return withItems(state, reorderItems(state.draft.items, action.oldIndex, action.newIndex))
    case 'PRODUCTION_RATE_UPDATED':
      return withItems(state, mapItem(state.draft.items, action.curatedTemplateItemId, item => ({
        ...item,
        rateInstallationQuantity: action.productionRate.rateInstallationQuantity,
        ratePerUnits: action.productionRate.ratePerUnits,
        rateUsageQuantity: action.productionRate.rateUsageQuantity
      })))
    case 'PRODUCTION_RATE_CLEARED':
      return withItems(state, mapItem(state.draft.items, action.curatedTemplateItemId, item => ({
        ...item,
        rateInstallationQuantity: undefined,
        ratePerUnits: undefined,
        rateUsageQuantity: undefined
      })))
    default:
      return state
  }
}

export const hasProductionRate = (item: CuratedTemplateItem): boolean => {
  return hasValue(item.rateInstallationQuantity) && hasValue(item.ratePerUnits) && hasValue(item.rateUsageQuantity)
}

export const formatProductionRate = (item: CuratedTemplateItem): string | undefined => {
  if (!hasProductionRate(item)) {
    return undefined
  }
  return `${item.rateInstallationQuantity} ${item.ratePerUnits} per ${item.rateUsageQuantity} ${item.units ?? ''}`.trim()
}

export const normalizeNameAliases = (nameAliases: string[]): string[] => {
  const seen = new Set<string>()
  return nameAliases.map(x => x.trim()).filter(alias => {
    if (alias === '' || seen.has(alias.toLowerCase())) {
      return false
    }
    seen.add(alias.toLowerCase())
    return true
  })
}

const normalize =(template: CuratedTemplate): CuratedTemplate => {
  return {
    ...template,
    name: template.name.trim(),
    description: template.description.trim(),
    aiInstructions: template.aiInstructions.trim(),
    items: reindex(sortedItems(template.items)).map(item => ({
      ...item,
      name: item.name.trim(),
      nameAliases: normalizeNameAliases(item.nameAliases)
    }))
  }
}

export const toUpsertRequest = (draft: CuratedTemplate): CuratedTemplateUpsertRequest => {
  const { lastUpdatedAt: _lastUpdatedAt, ...request } = normalize(draft)
  return request
}

const sameNumber = (a: number | null | undefined, b: number | null | undefined): boolean => {
  return (a ?? null) === (b ?? null)
}

const sameText = (a: string | null | undefined, b: string | null | undefined): boolean => {
  return (a ?? '') === (b ?? '')
}

const sameStrings = (a: string[], b: string[]): boolean => {
  return a.length === b.length && a.every((value, index) => value === b[index])
}

const itemsAreEqual = (a: CuratedTemplateItem, b: CuratedTemplateItem): boolean => {
  return a.curatedTemplateItemId === b.curatedTemplateItemId
    && a.name === b.name
    && sameStrings(a.nameAliases, b.nameAliases)
    && a.class === b.class
    && a.sortIndex === b.sortIndex
    && sameNumber(a.cost, b.cost)
    && sameText(a.units, b.units)
    && sameNumber(a.rateInstallationQuantity, b.rateInstallationQuantity)
    && sameText(a.ratePerUnits, b.ratePerUnits)
    && sameNumber(a.rateUsageQuantity, b.rateUsageQuantity)
}

export const isChanged = (state: EditorState): boolean => {
  const original = normalize(state.original)
  const draft = normalize(state.draft)

  if (original.name !== draft.name || original.description !== draft.description) {
    return true
  }
  if (original.availability !== draft.availability || original.aiInstructions !== draft.aiInstructions) {
    return true
  }
  if (original.items.length !== draft.items.length) {
    return true
  }
  return !original.items.every((item, index) => itemsAreEqual(item, draft.items[index]))
}

export const getSaveDisabledReason = (state: EditorState): string => {
  if (state.draft.name.trim() === '') {
    return 'Enter a name for this template'
  }
  if (state.draft.items.some(x => x.name.trim() === '')) {
    return 'Every item needs a name'
  }
  if (!isChanged(state)) {
    return 'There are no changes to save'
  }
  return ''
}
